import React from 'react';
import { Switch, Space, Alert } from 'antd';
import securityStyles from '../security/security.module.css';
import { FeedbackCollection } from './FeedbackCollection';
import * as translations from './strings';
import { getLangKey } from '../../../../models/Language';
import * as dayjs from 'dayjs';
import styles from './style.module.css';

const strings = translations[getLangKey()];

export const getIsAcceptingFeedback = ({
  acceptingFeedback,
  feedbackDeadlineAllowed,
  acceptingFeedbackEndDate
}: {
  acceptingFeedback: boolean;
  feedbackDeadlineAllowed: boolean;
  acceptingFeedbackEndDate: string | null;
}): boolean =>
  feedbackDeadlineAllowed && acceptingFeedbackEndDate
    ? dayjs(acceptingFeedbackEndDate).isAfter(dayjs())
    : !!acceptingFeedback;

export const getCanCreateFeedback = ({
  isAcceptingFeedback,
  isClient
}: {
  isAcceptingFeedback: boolean;
  isClient: boolean;
}): boolean => {
  if (!isClient) return true;
  return isAcceptingFeedback;
};

type Props = {
  feedbackDeadlineAllowed: boolean;
  acceptingFeedbackAllowed: boolean;
  isAcceptingFeedback: boolean;
  acceptingFeedbackEndDate: string | null;
  setAcceptingFeedbackEndDate: (date: string | null) => void;
  setShowFeedbackDateModal: (show: boolean) => void;
  showFeedbackDateModal: boolean;
  container: HTMLDivElement;
  billing: boolean;
  organizationId: number;
  owner: { email: string } | null;
  acceptingFeedback?: boolean;
  acceptingFeedbackLoading?: boolean;
  onAcceptingFeedbackChange?: (checked: boolean) => void;
  onScheduleEndDate?: (date: string) => void;
  onRemoveSchedule?: () => void;
  onPauseFeedbackCollection?: () => void;
  scheduleLoading?: boolean;
  compact?: boolean;
};

export const AcceptingFeedback = ({
  feedbackDeadlineAllowed,
  acceptingFeedbackAllowed,
  isAcceptingFeedback,
  acceptingFeedbackEndDate,
  setAcceptingFeedbackEndDate,
  setShowFeedbackDateModal,
  showFeedbackDateModal,
  container,
  billing,
  organizationId,
  owner,
  acceptingFeedback,
  acceptingFeedbackLoading,
  onAcceptingFeedbackChange,
  onScheduleEndDate,
  onRemoveSchedule,
  onPauseFeedbackCollection,
  scheduleLoading,
  compact = false
}: Props) => {
  if (!acceptingFeedbackAllowed) return null;

  const scheduleControlsFeedback =
    !!acceptingFeedbackEndDate && feedbackDeadlineAllowed;

  return (
    <>
      {!compact && (
        <>
          <h3 className={securityStyles.basicHeader}>
            {strings.acceptingFeedbackHeader}
          </h3>
          <p>{strings.acceptingFeedbackMessage}</p>
        </>
      )}
      <Space direction="vertical" className={styles.feedbackSwitches}>
        <Space direction="horizontal">
          <Switch
            className={securityStyles.acceptingFormItem}
            checked={acceptingFeedback}
            loading={acceptingFeedbackLoading}
            disabled={scheduleControlsFeedback || !onAcceptingFeedbackChange}
            onChange={onAcceptingFeedbackChange}
            title={
              scheduleControlsFeedback
                ? strings.scheduledDate
                : strings.enableDisable
            }
          />
          <Alert
            className={styles.feedbackStatusAlert}
            type={isAcceptingFeedback ? 'success' : 'warning'}
            showIcon
            message={
              <div>
                <div className={styles.feedbackStatusMessage}>
                  {isAcceptingFeedback ? strings.enabled : strings.paused}
                </div>
                {!isAcceptingFeedback && (
                  <div className={styles.feedbackStatusHint}>
                    {strings.acceptingFeedbackHint}
                  </div>
                )}
              </div>
            }
          />
        </Space>

        <FeedbackCollection
          container={container}
          feedbackDeadlineAllowed={feedbackDeadlineAllowed}
          acceptingFeedbackEndDate={acceptingFeedbackEndDate}
          setAcceptingFeedbackEndDate={setAcceptingFeedbackEndDate}
          onScheduleEndDate={onScheduleEndDate || (() => {})}
          onRemoveSchedule={onRemoveSchedule || (() => {})}
          onPauseFeedbackCollection={onPauseFeedbackCollection || (() => {})}
          setOpen={setShowFeedbackDateModal}
          open={showFeedbackDateModal}
          isAcceptingFeedback={isAcceptingFeedback}
          billing={billing}
          organizationId={organizationId}
          owner={owner}
          scheduleLoading={scheduleLoading}
        />
      </Space>
    </>
  );
};
